import { Activity } from "../../models/activity";
import TrainingModule from "../../models/trainingModule";
import {
  FocusArea,
  ModuleSelectionState,
  TrainingModuleOption,
} from "../../types/actionPlan";
import { OptionItem } from "./PreferenceStep";

export const getInclusionPulseOptions = (
  t: (str: string) => string
): OptionItem[] => [
  { label: t("setup.survey.frequencyOptions.1Month"), value: "30" },
  { label: t("setup.survey.frequencyOptions.3Months"), value: "90" },
  { label: t("setup.survey.frequencyOptions.6Months"), value: "180" },
  { label: t("setup.survey.frequencyOptions.9Months"), value: "270" },
];

export const getDeliveryIntervalOptions = (
  t: (str: string) => string
): OptionItem[] => [
  { label: t("setup.training.intervalOptions.1perQuarter"), value: "1" },
  { label: t("setup.training.intervalOptions.2perQuarter"), value: "2" },
  { label: t("setup.training.intervalOptions.3perQuarter"), value: "3" },
];

export const getFocusAreaOptions = (
  t: (str: string) => string
): OptionItem[] => [
  {
    label: t("setup.training.focusAreas.ALDA_RECOMMENDED.label"),
    value: FocusArea.AldaRecommended,
    info: t("setup.training.focusAreas.ALDA_RECOMMENDED.info"),
  },
  {
    label: t("setup.training.focusAreas.COMPLIANCE.label"),
    value: FocusArea.Compliance,
    info: t("setup.training.focusAreas.COMPLIANCE.info"),
  },
  {
    label: t("setup.training.focusAreas.CORE_INCLUSION.label"),
    value: FocusArea.CoreInclusion,
    info: t("setup.training.focusAreas.CORE_INCLUSION.info"),
  },
  {
    label: t("setup.training.focusAreas.INCLUSIVE_LEADERSHIP.label"),
    value: FocusArea.InclusiveLeadership,
    info: t("setup.training.focusAreas.INCLUSIVE_LEADERSHIP.info"),
  },
  {
    label: t("setup.training.focusAreas.AUTHENTICITY_AND_MASKING.label"),
    value: FocusArea.AuthenticityAndMasking,
    info: t("setup.training.focusAreas.AUTHENTICITY_AND_MASKING.info"),
  },
  {
    label: t("setup.training.focusAreas.PSYCHOLOGICAL_SAFETY.label"),
    value: FocusArea.PsychologicalSafety,
    info: t("setup.training.focusAreas.PSYCHOLOGICAL_SAFETY.info"),
  },
  {
    label: t("setup.training.focusAreas.STRATEGIC_LEADERSHIP.label"),
    value: FocusArea.StrategicLeadership,
    info: t("setup.training.focusAreas.STRATEGIC_LEADERSHIP.info"),
  },
];

/**
 * Filters activities to only include training-related types
 * (Training and LeadershipTraining)
 */
export const filterTrainingActivities = (
  activities: Activity[]
): Activity[] => {
  return activities.filter((activity) => activity.isTrainingActivity());
};

/**
 * Filters activities to exclude training-related types
 */
export const filterNonTrainingActivities = (
  activities: Activity[]
): Activity[] => {
  return activities.filter((activity) => !activity.isTrainingActivity());
};

/**
 * Determines the selection state of each training module based on current activities
 * Activity in action plan -> Module is Being Used
 * Activity not in action plan -> Module is Unselected
 */
export const getTrainingModuleStates = (
  activities: Activity[],
  trainingModules: TrainingModule[]
): TrainingModuleOption[] => {
  return trainingModules.map((module) => {
    const isBeingUsed = activities.some((activity) => {
      const extraProps = activity.extraProperties;
      return (
        activity.isTrainingActivity() &&
        extraProps.training_module?.id === module.referenceId
      );
    });

    const selectionState = isBeingUsed
      ? ModuleSelectionState.BeingUsed
      : ModuleSelectionState.Unselected;

    return {
      trainingModule: module,
      state: selectionState,
    };
  });
};
